import React, { useState, useMemo } from 'react'
import { Button, Modal, useModal, toast, Text, Spacer, Flexbox, Badge } from '@common'
import { IMPORT_COMPLETED, ERROR_UPLOAD_FILE } from '@constants'
import ImportInput from './ImportInput'
import {
  useImportAssetsMutation,
  useValidateAssetsMutation,
  useImportCategoriesMutation,
  useImportCompaniesMutation,
  useImportGLPostsMutation,
  useImportFoldersMutation,
  useImportInsuranceMutation,
  useImportMaintenanceMutation,
  useImportSegmentsMutation,
  useImportTasksMutation,
  useImportVendorsMutation,
  AssetValidationResult,
  useFetchFieldParmsQuery,
  useGetFieldsQuery,
} from '@api'
import { buildAssetImportOptions } from './columns/assets'
import { ImportType } from './importTypes'
import ImportMap from './ImportMap'
import ValidationResults from './ValidationResults'

interface ImportDataProps {
  action: ImportType
  title?: string
  modalDesc?: React.ReactNode
  uploadDesc?: React.ReactNode
  onUploadCompletion?: () => void
}

const ImportData = ({
  action,
  title,
  modalDesc = '',
  uploadDesc = '',
  onUploadCompletion = () => {},
}: ImportDataProps) => {
  const [file, setFile] = useState(null)
  const [mapping, setMapping] = useState({})
  const [isMappingLoading, setIsMappingLoading] = useState(false)
  const [validationResult, setValidationResult] = useState<AssetValidationResult | null>(null)
  const [hasValidated, setHasValidated] = useState(false)
  const { modalIsOpen, triggerModal, closeModal } = useModal()

  const [importAssets, { isLoading: isLoadingAssets }] = useImportAssetsMutation()
  const [validateAssets, { isLoading: isValidating }] = useValidateAssetsMutation()
  const [
    importCategories,
    { isLoading: isLoadingCategories },
  ] = useImportCategoriesMutation()
  const [importCompanies, { isLoading: isLoadingCompanies }] = useImportCompaniesMutation()
  const [importGLPosts, { isLoading: isLoadingGLPosts }] = useImportGLPostsMutation()
  const [importFolders, { isLoading: isLoadingFolders }] = useImportFoldersMutation()
  const [importInsurance, { isLoading: isLoadingInsurance }] = useImportInsuranceMutation()
  const [
    importMaintenance,
    { isLoading: isLoadingMaintenance },
  ] = useImportMaintenanceMutation()
  const [importSegments, { isLoading: isLoadingSegments }] = useImportSegmentsMutation()
  const [importTasks, { isLoading: isLoadingTasks }] = useImportTasksMutation()
  const [importVendors, { isLoading: isLoadingVendors }] = useImportVendorsMutation()

  const { data: asFieldParms, isLoading: asFieldParmsLoading } = useFetchFieldParmsQuery(
    { tablePrefix: 'AS', pageNumber: 1, itemsPerPage: 100, filter: {} },
    { skip: action !== 'assets' }
  )
  const { data: dpFieldParms, isLoading: dpFieldParmsLoading } = useFetchFieldParmsQuery(
    { tablePrefix: 'DP', pageNumber: 1, itemsPerPage: 50, filter: {} },
    { skip: action !== 'assets' }
  )
  const { data: listFields, isLoading: listFieldsLoading } = useGetFieldsQuery(
    undefined,
    { skip: action !== 'assets' }
  )

  const dynamicAssetOptions = useMemo(() => {
    if (action !== 'assets' || !asFieldParms || !dpFieldParms || !listFields) return null
    const map = new Map<string, string>()
    asFieldParms.list.forEach(f => map.set(f.PM_NAME, f.PM_PMT))
    dpFieldParms.list.forEach(f => map.set(f.PM_NAME, f.PM_PMT))
    listFields.forEach(f => map.set(f.Name, f.Value))
    return buildAssetImportOptions(map)
  }, [action, asFieldParms, dpFieldParms, listFields])

  const isAssetOptionsLoading = action === 'assets' && (asFieldParmsLoading || dpFieldParmsLoading || listFieldsLoading)

  const isImportLoading = useMemo(() => {
    return (
      isLoadingAssets ||
      isLoadingCategories ||
      isLoadingCompanies ||
      isLoadingGLPosts ||
      isLoadingFolders ||
      isLoadingInsurance ||
      isLoadingMaintenance ||
      isLoadingSegments ||
      isLoadingTasks ||
      isLoadingVendors
    )
  }, [
    isLoadingAssets,
    isLoadingCategories,
    isLoadingCompanies,
    isLoadingGLPosts,
    isLoadingFolders,
    isLoadingInsurance,
    isLoadingMaintenance,
    isLoadingSegments,
    isLoadingTasks,
    isLoadingVendors,
  ])

  const isLoading = isImportLoading || isMappingLoading || isValidating

  const getCurrentLoadingState = () => {
    switch (action) {
      case 'assets':
        return isLoadingAssets
      case 'categories':
        return isLoadingCategories
      case 'companies':
        return isLoadingCompanies
      case 'glposts':
        return isLoadingGLPosts
      case 'folders':
        return isLoadingFolders
      case 'insurance':
        return isLoadingInsurance
      case 'maintenance':
        return isLoadingMaintenance
      case 'segments':
        return isLoadingSegments
      case 'tasks':
        return isLoadingTasks
      case 'vendors':
        return isLoadingVendors
      default:
        return false
    }
  }

  const clearFile = React.useCallback(() => {
    setFile(null)
    setMapping({})
    setValidationResult(null)
    setHasValidated(false)
  }, [])

  const onClose = React.useCallback(() => {
    setFile(null)
    setMapping({})
    setValidationResult(null)
    setHasValidated(false)
    closeModal()
  }, [closeModal])

  const updateMapping = React.useCallback(mapping => {
    setMapping(mapping)
  }, [])

  const handleMappingLoadingChange = React.useCallback((loading: boolean) => {
    setIsMappingLoading(loading)
  }, [])

  const handleValidate = React.useCallback(
    async () => {
      if (action !== 'assets') {
        setHasValidated(true)
        setValidationResult({ IsValid: true, TotalErrors: 0, Errors: [] })
        return
      }

      try {
        const formData = new FormData()
        formData.append('file', file)

        const keys = Object.keys(mapping)
        const lookup = {}

        keys.forEach(key => {
          const item = mapping[key]
          if (!item) return
          lookup[item.value] = key
        })

        const result = await validateAssets({ file: formData, mapping: lookup }).unwrap()

        setValidationResult(result)
        setHasValidated(true)

        const errorCount = result.TotalErrors || 0
        const hasErrors = !result.IsValid || errorCount > 0 || (result.Errors && result.Errors.length > 0)

        if (!hasErrors) {
          toast.success('Validation passed! No errors found.')
        } else {
          toast.warn(`Validation found ${errorCount} error${errorCount === 1 ? '' : 's'}.`)
        }
      } catch (error: any) {
        console.error(error)
        setHasValidated(true)

        if (error?.data?.message) {
          setValidationResult({
            IsValid: false,
            TotalErrors: 1,
            Errors: [{
              AssetId: 'Validation Error',
              Errors: [error.data.message]
            }]
          })
          toast.error(error.data.message)
        } else {
          toast.error('Error validating file')
        }
      }
    },
    [file, mapping, validateAssets, action]
  )

  const uploadFile = React.useCallback(
    async e => {
      try {
        e.preventDefault()

        if (action === 'assets' && validationResult && !validationResult.IsValid) {
          toast.error('Cannot upload: Validation errors must be fixed first')
          return
        }
        const formData = new FormData()
        formData.append('file', file)

        const keys = Object.keys(mapping)
        const lookup = {}

        keys.forEach(key => {
          const item = mapping[key]
          if (!item) return
          lookup[item.value] = key
        })

        // Use the appropriate mutation based on action type
        switch (action) {
          case 'assets':
            await importAssets({ file: formData, mapping: lookup }).unwrap()
            break
          case 'categories':
            await importCategories({ file: formData, mapping: lookup }).unwrap()
            break
          case 'companies':
            await importCompanies({ file: formData, mapping: lookup }).unwrap()
            break
          case 'glposts':
            await importGLPosts({ file: formData, mapping: lookup }).unwrap()
            break
          case 'folders':
            await importFolders({ file: formData, mapping: lookup }).unwrap()
            break
          case 'insurance':
            await importInsurance({ file: formData, mapping: lookup }).unwrap()
            break
          case 'maintenance':
            await importMaintenance({ file: formData, mapping: lookup }).unwrap()
            break
          case 'segments':
            await importSegments({ file: formData, mapping: lookup }).unwrap()
            break
          case 'tasks':
            await importTasks({ file: formData, mapping: lookup }).unwrap()
            break
          case 'vendors':
            await importVendors({ file: formData, mapping: lookup }).unwrap()
            break
          default:
            throw new Error('Invalid import type')
        }

        setFile(null)
        setMapping({})
        onClose()
        onUploadCompletion()
        toast.success(IMPORT_COMPLETED)
      } catch (error: any) {
        console.error(error)
        toast.error(error?.data?.message ?? ERROR_UPLOAD_FILE)
      }
    },
    [
      file,
      action,
      mapping,
      validationResult,
      onUploadCompletion,
      onClose,
      importAssets,
      importCategories,
      importCompanies,
      importGLPosts,
      importFolders,
      importInsurance,
      importMaintenance,
      importSegments,
      importTasks,
      importVendors,
    ]
  )

  const isActionDisabled = file === null || file === undefined || file === '' || isLoading || (action === 'assets' && (!hasValidated || (validationResult && !validationResult.IsValid)))
  const currentActionLoading = getCurrentLoadingState()

  const getLoadingMessage = () => {
    if (currentActionLoading) return 'Importing data, please wait...'
    if (isMappingLoading) return 'Processing file and mapping columns...'
    if (isValidating) return 'Validating assets, please wait...'
    return null
  }

  return (
    <React.Fragment>
      <Button appearance="default" onClick={triggerModal} disabled={isLoading}>
        {isLoading ? 'Processing...' : 'Import'}
      </Button>
      <Modal
        isOpen={modalIsOpen}
        close={onClose}
        onSubmit={e => uploadFile(e)}
        actionDisabled={isActionDisabled}
        actionLabel={currentActionLoading ? 'Importing...' : 'Upload and Continue'}
        title={title}
        width={800}
      >
        <Text p>{modalDesc}</Text>
        <Spacer y="default" />
        <ImportInput
          file={file}
          onRemove={clearFile}
          uploadDesc={uploadDesc}
          onChange={e => {
            setFile(e.target.files[0])
            setHasValidated(false)
            setValidationResult(null)
          }}
        />
        <ImportMap
          file={file}
          entity={action}
          updateMapping={updateMapping}
          onLoadingChange={handleMappingLoadingChange}
          options={dynamicAssetOptions ?? undefined}
          isOptionsLoading={isAssetOptionsLoading}
        />
        
        {action === 'assets' && file && (
          <>
            <Spacer y="default" />
            <Flexbox justifyContent="center">
              <Button
                appearance="primary"
                onClick={handleValidate}
                disabled={isLoading || !file}
                size="large"
              >
                {isValidating ? 'Validating...' : 'Validate'}
              </Button>
            </Flexbox>
          </>
        )}
        
        {validationResult && hasValidated && (
          <>
            <Spacer y="default" />
            <ValidationResults
              errors={validationResult.Errors}
              totalErrors={validationResult.TotalErrors}
            />
          </>
        )}

        {getLoadingMessage() && (
          <>
            <Spacer y="default" />
            <Text p style={{ textAlign: 'center', fontStyle: 'italic' }}>
              {getLoadingMessage()}
            </Text>
          </>
        )}
      </Modal>
    </React.Fragment>
  )
}
export default ImportData
